/**
 * KETCHUP_LOADER.JSX - Loader avec bouteille de ketchup SVG
 * 
 * Affiche une bouteille de ketchup qui se remplit pendant le chargement
 * des ressources 3D (modèles, textures, matcaps).
 * 
 * FONCTIONNALITÉS:
 * - Image SVG de bouteille de ketchup
 * - Remplissage avec diagonales (pattern SVG)
 * - Progression de 0 à 100%
 * - Animation d'explosion de particules à 100%
 * - Transition "Chargement..." → "Go!"
 * - Disparition automatique après l'animation
 * 
 * TECHNIQUE:
 * - useState pour simuler la progression (devient 100% après un délai)
 * - Manipulation SVG pour le remplissage
 * - Particules CSS pures (pas de canvas)
 * - Animations hardware-accelerated
 * 
 * UTILISATION:
 * Dans App.jsx, placer ce composant en dehors du Canvas :
 * <KetchupLoader />
 * <Canvas>...</Canvas>
 */

import { useState, useEffect, useRef } from 'react'
import { useSounds } from '../hooks/useSounds'

// Import des SVG du loader
import loaderBack from '/static/images/loader_back.svg'
import loaderMiddle from '/static/images/loader_middle.svg'
import loaderFront from '/static/images/loader_front.svg'
import loaderLoading from '/static/images/loader_loading.svg'
import loaderStart from '/static/images/loader_start.svg'
// Note: loader_mask.svg utilisé via CSS mask-image (invisible)

/**
 * KetchupLoader Component
 * 
 * @param {number} progress - Progression du chargement (0-100)
 * @param {Function} onStart - Callback appelé quand l'utilisateur clique sur "Go!"
 * @returns {JSX.Element} Interface de chargement animée
 */
export function KetchupLoader({ progress = 0, onStart }) {
  // Hook pour le système audio
  const { play } = useSounds()
  // État local pour une animation plus fluide
  const [smoothProgress, setSmoothProgress] = useState(0)
  
  // États de l'animation
  const [isComplete, setIsComplete] = useState(false)
  const [showExplosion, setShowExplosion] = useState(false)
  const [isMorphing, setIsMorphing] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  
  // Référence pour le container avec masque CSS
  const middleContainerRef = useRef(null)
  
  // ============================================
  // LISSAGE DE LA PROGRESSION
  // ============================================
  useEffect(() => {
    const timer = setTimeout(() => {
      setSmoothProgress(progress)
      
      // Détection de la fin du chargement
      if (progress >= 100 && !isComplete) {
        setIsComplete(true)
        // Déclencher le morphing immédiatement
        setIsMorphing(true)
        // Attendre 300ms puis déclencher l'explosion
        setTimeout(() => {
          setShowExplosion(true)
        }, 300)
      }
    }, 50)
    
    return () => clearTimeout(timer)
  }, [progress, isComplete])
  
  // ============================================
  // GESTION DU CLIC SUR "GO!"
  // ============================================
  const handleGoClick = () => {
    // Jouer le son "reveal" au début du fadeout
    play('reveal')
    
    setIsExiting(true)
    // Attendre la fin de l'animation de sortie puis notifier le parent
    setTimeout(() => {
      if (onStart) {
        onStart()
      }
    }, 800) // Durée de l'animation de sortie
  }
  
  // ============================================
  // MISE À JOUR DU MASQUE SVG (SE VIDE DU HAUT VERS LE BAS)
  // ============================================
  useEffect(() => {
    if (middleContainerRef.current) {
      // Le masque SVG descend pour "vider" la bouteille
      // À 0% de progression : translateY(-100%), bouteille pleine
      // À 100% de progression : translateY(0%), bouteille vide
      const translateY = -100 + smoothProgress
      middleContainerRef.current.style.transform = `translateY(${translateY}%)`
    }
  }, [smoothProgress])
  
  // ============================================
  // GÉNÉRATION DES PARTICULES
  // ============================================
  // 20 particules réparties en cercle
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    angle: (i / 20) * Math.PI * 2, // Répartition circulaire
    distance: 100 + Math.random() * 100, // Distance aléatoire
    delay: Math.random() * 0.2 // Délai aléatoire
  }))

  return (
    <div className={`ketchup-loader ${isComplete ? 'complete' : ''} ${isExiting ? 'exiting' : ''}`}>
      {/* ============================================ */}
      {/* SYSTÈME MULTI-COUCHES SVG */}
      {/* ============================================ */}
      <div className="bottle-container">
        <div className="loader-bottle-layers">
          {/* Layer 1 : Arrière-plan (ketchup au sol) */}
          <img 
            src={loaderBack} 
            alt="" 
            className="loader-layer"
            style={{ zIndex: 1 }}
          />
          
          {/* Layer 2 : Middle avec SVG mask inline (effet de vague) */}
          <svg 
            className="loader-svg-container" 
            viewBox="0 0 450.8 634.72"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <mask id="ketchup-mask">
                <rect width="450.8" height="634.72" fill="white"/>
                <g 
                  ref={middleContainerRef}
                  className="loader-svg-mask-element"
                  style={{ transform: 'translateY(-100%)' }}
                >
                  <path fill="black" d="M350.01,550.37V8.4H58.45v541.97l31.91,5.84s12.07-11.74,22.16-12.87c28.38-3.17,43.93,11.57,89.65,6.86,25.62-2.64,90.44-16.36,106.07-4.96,16.31,11.89,41.77,5.13,41.77,5.13Z"/>
                </g>
              </mask>
            </defs>
            <image 
              href={loaderMiddle}
              width="450.8" 
              height="634.72"
              mask="url(#ketchup-mask)"
            />
          </svg>
          
          {/* Layer 3 : Premier plan (contour de la bouteille) */}
          <img 
            src={loaderFront} 
            alt="" 
            className="loader-layer"
            style={{ zIndex: 3 }}
          />
          
          {/* ============================================ */}
          {/* TEXTES ANIMÉS - MORPHING LOADING -> START */}
          {/* ============================================ */}
          <div className="loader-text-container">
            {/* Texte LOADING visible pendant le chargement */}
            <img 
              src={loaderLoading} 
              alt="" 
              className={`loader-text-svg loader-text-loading ${isMorphing ? 'morphing' : ''}`}
            />
            
            {/* Texte START caché puis apparaît au morphing */}
            <img 
              src={loaderStart} 
              alt="" 
              className={`loader-text-svg loader-text-start ${isMorphing ? 'morphing' : ''} ${isHovering ? 'hovering' : ''}`}
            />
            
            {/* Bouton transparent cliquable quand le chargement est terminé */}
            {isComplete && (
              <button 
                className="loader-start-button visible" 
                onClick={handleGoClick}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                disabled={isExiting}
                aria-label="Démarrer l'application"
              />
            )}
          </div>
        </div>
        
        {/* ============================================ */}
        {/* PARTICULES D'EXPLOSION */}
        {/* ============================================ */}
        {showExplosion && (
          <div className="particles">
            {particles.map((p) => (
              <div
                key={p.id}
                className="particle"
                style={{
                  '--angle': `${p.angle}rad`,
                  '--distance': `${p.distance}px`,
                  '--delay': `${p.delay}s`
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * NOTES D'IMPLÉMENTATION:
 * 
 * 1. PROGRESSION:
 *    - useProgress de Drei track automatiquement tous les useGLTF/useTexture
 *    - On lisse la progression avec un état local pour éviter les saccades
 * 
 * 2. REMPLISSAGE SVG:
 *    - On manipule directement les attributs y et height du rectangle
 *    - Le clipPath assure que le ketchup reste dans la bouteille
 *    - Pattern SVG pour les diagonales (performant et scalable)
 * 
 * 3. SÉQUENCE D'ANIMATION:
 *    - 0-99% : Remplissage progressif + "Chargement..." qui pulse
 *    - 100% : Texte → "Go!" + explosion de particules + rebond
 *    - Après 1.5s : Fondu et disparition du loader
 * 
 * 4. PARTICULES:
 *    - 20 particules CSS pures (pas de canvas)
 *    - Répartition circulaire via trigonométrie
 *    - CSS custom properties pour l'angle et la distance
 *    - Hardware-accelerated (transform + opacity)
 * 
 * 5. PERFORMANCE:
 *    - SVG plutôt que PNG (léger, scalable)
 *    - Animations CSS plutôt que JS (60fps garantis)
 *    - Pas de re-render React inutile
 */

